import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { type AlbumScore, type BrowseFacet, type Track, albumScore, browseGroups, rank } from "@trackmark/core";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { Spark } from "../design/Spark.js";
import shared from "./Rankings.module.css";
import styles from "./AlbumRankings.module.css";

export interface AlbumRow {
  id: number;
  title: string;
  artist: string;
  year: number | null;
  /** 'album' | 'ep' | 'single' | 'compilation' | 'live' — from the release, not guessed. */
  primaryType: string;
  /** The modal genre of the album's tracks; null when nothing is mapped yet. */
  genre: string | null;
  cohesion: number | null;
  art: string | null;
  /** Core tracks in track order. The shared albumScore() turns these into a number, or refuses to. */
  tracks: Track[];
}

/**
 * §5 of the handoff sets these widths at 30px rows; ruling I2 raised the row height to 32 with
 * 24px artwork across every table, so the ARTWORK cell stays 40 and the art inside it is a token.
 */
const ALBUM_COLUMNS = "66px 40px 1fr 210px 132px 56px 52px";
const SPARK_WIDTH = 132;

const FACETS: { id: BrowseFacet; label: string }[] = [
  { id: "decade", label: "DECADE" },
  { id: "genre", label: "GENRE" },
  { id: "artist", label: "ARTIST" },
  { id: "band", label: "SCORE BANDS" },
];

/** Excluded from album rankings by default, and complete by definition — ruling J6/J11. */
const SIDELINED = new Set(["single", "compilation"]);

export interface AlbumRankingsProps {
  albums: AlbumRow[];
}

interface Scored {
  album: AlbumRow;
  score: AlbumScore;
}

/**
 * Album rankings, and the same query browsed as covers beneath them.
 *
 * Both surfaces read one filtered list. They are not two queries that happen to agree — the covers
 * are the table, regrouped, which is what makes "the artwork is the index" true rather than a
 * caption. There is no spine or shelf view here and there will not be one (docs/DESIGN-CONFLICTS.md
 * D1): it was rejected outright, and a component gallery is not a route back in.
 */
export function AlbumRankings({ albums }: AlbumRankingsProps) {
  const [facet, setFacet] = useState<BrowseFacet>("decade");
  const [completeOnly, setCompleteOnly] = useState(false);
  const [includeSidelined, setIncludeSidelined] = useState(false);

  const scored = useMemo<Scored[]>(
    () =>
      albums
        .filter((a) => includeSidelined || !SIDELINED.has(a.primaryType))
        .map((album) => ({ album, score: albumScore(album.tracks, album.cohesion) }))
        .filter((s) => !completeOnly || s.score.complete),
    [albums, completeOnly, includeSidelined],
  );

  const ranked = useMemo(() => rank(scored, (s) => s.score.score), [scored]);

  const groups = useMemo(
    () =>
      browseGroups(scored, facet, {
        year: (s) => s.album.year,
        genre: (s) => s.album.genre,
        artist: (s) => s.album.artist,
        score: (s) => s.score.score,
      }),
    [scored, facet],
  );

  return (
    <>
      <div className={shared.filters}>
        <button
          type="button"
          className={shared.filter}
          data-active={completeOnly}
          data-testid="complete-only"
          onClick={() => setCompleteOnly((v) => !v)}
        >
          COMPLETE ONLY
        </button>
        <button
          type="button"
          className={shared.filter}
          data-active={includeSidelined}
          data-testid="include-sidelined"
          onClick={() => setIncludeSidelined((v) => !v)}
        >
          INCLUDE SINGLES &amp; COMPILATIONS
        </button>
        <span className={shared.count} data-testid="row-count">
          {scored.length.toLocaleString()} ALBUMS
        </span>
      </div>

      <div className={styles.tableSlot}>
        <RankTable
          rows={ranked}
          columns={ALBUM_COLUMNS}
          rowKey={(r) => r.item.album.id}
          rowProps={(r) => ({
            // An incomplete album is the same statement as an unrated song: this needs you.
            "data-state": r.item.score.complete ? undefined : "unrated",
            "data-rank": String(r.rank ?? ""),
            "data-testid": "album-row",
          })}
          header={
            <>
              <div>RANK</div>
              <div />
              <div>ALBUM</div>
              <div>ARTIST</div>
              <div>SPARK</div>
              <div style={{ textAlign: "right" }}>SCORE</div>
              <div style={{ textAlign: "right" }}>YEAR</div>
            </>
          }
          renderRow={(r) => (
            <>
              <div className={table.rank} data-repeat={r.repeat || undefined} data-unranked={r.rank === null || undefined}>
                {r.rank ?? "—"}
              </div>
              <div>
                {r.item.album.art ? (
                  <img className={table.art} src={r.item.album.art} alt="" />
                ) : (
                  <div className={table.artFallback}>{r.item.album.title.slice(0, 8)}</div>
                )}
              </div>
              <Link
                to="/album/$albumId"
                params={{ albumId: String(r.item.album.id) }}
                className={table.titleLink}
                data-testid="album-link"
              >
                {r.item.album.title}
              </Link>
              <div className={table.secondary}>{r.item.album.artist}</div>
              <Spark
                ratings={r.item.album.tracks.map((t) => t.rating)}
                width={SPARK_WIDTH}
                label={`${r.item.album.title}: ${r.item.score.ratedCoreCount} of ${r.item.score.coreCount} core tracks rated`}
              />
              {r.item.score.complete ? (
                <div className={table.score} data-testid="album-table-score">
                  {r.item.score.score!.toFixed(2)}
                </div>
              ) : (
                // No provisional number, ever — not even a greyed one. The count is the state.
                <div className={styles.incomplete} data-testid="album-incomplete">
                  {r.item.score.ratedCoreCount}/{r.item.score.coreCount}
                </div>
              )}
              <div className={table.meta}>{r.item.album.year ?? "—"}</div>
            </>
          )}
        />
      </div>

      <div className={styles.covers}>
        <div className={styles.coversHead}>
          <div className={styles.coversLabel}>
            COVERS · SAME QUERY, BROWSED BY {FACETS.find((f) => f.id === facet)!.label}
          </div>
          <div className={styles.facets}>
            {FACETS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={shared.filter}
                data-active={facet === f.id}
                data-testid={`facet-${f.id}`}
                onClick={() => setFacet(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.well} data-testid="covers">
          {groups.map((group) => (
            <div key={group.key} className={styles.group} data-testid="cover-group">
              <div className={styles.groupLabel} data-testid="group-label">
                {group.label}
              </div>
              <div className={styles.grid}>
                {group.items.map(({ album, score }) => (
                  <Link
                    key={album.id}
                    to="/album/$albumId"
                    params={{ albumId: String(album.id) }}
                    className={styles.cover}
                    data-testid="cover"
                  >
                    <div className={styles.artWrap} data-incomplete={score.complete ? undefined : "true"}>
                      {album.art ? (
                        <img className={styles.art} src={album.art} alt="" />
                      ) : (
                        <div className={styles.artFallback}>{album.title}</div>
                      )}
                    </div>
                    {/* Two lines, clipped at the box — never a mid-word ellipsis. */}
                    <div className={styles.coverTitle}>{album.title}</div>
                    <div className={styles.coverScore} data-incomplete={score.complete ? undefined : "true"}>
                      {score.complete ? score.score!.toFixed(2) : "—"}
                    </div>
                  </Link>
                ))}
              </div>
              <div className={styles.groupCount}>
                {group.items.length} {group.items.length === 1 ? "ALBUM" : "ALBUMS"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
